import {
  LoyaltyTier,
  TIER_ORDER,
  TIER_THRESHOLDS,
  nextTierFor,
  tierFromBalance,
} from '@/constants/loyalty'

/**
 * Loyalty mock. Every member gets a deterministic point balance derived from
 * a stable hash of their id; the tier, progress, and lifetime stats are then
 * computed from that single balance. This keeps the [[MemberListPage]] tier
 * chip and the [[MemberDetailsPage]] loyalty band consistent for the same
 * member across refreshes and routes.
 *
 * Swap to real `/loyalty/members/:id/*` calls once the BE ships — see
 * [[./index]] for the toggle (`VITE_LOYALTY_USE_MOCK`).
 */

const hash = (input: string): number => {
  let h = 0
  for (let i = 0; i < input.length; i++) {
    h = (h * 31 + input.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

/**
 * Balance distribution shaped to give a realistic spread across tiers:
 * roughly 30 % Beginning · 35 % Silver · 25 % Gold · 10 % Platinum, with
 * exact balances varying inside each bucket.
 */
const BALANCE_BUCKETS: number[] = [
  120,
  280,
  410, // Beginning
  650,
  980,
  1340,
  1780, // Silver
  2150,
  2690,
  3450, // Gold
  5240, // Platinum
]

const getMockBalance = (memberId: string | number): number => {
  const key = String(memberId)
  const h = hash(key)
  const bucketIdx = h % BALANCE_BUCKETS.length
  // Spread within the bucket so two members with the same bucket get slightly
  // different values rather than identical balances.
  const jitter = (h >> 4) % 90
  return BALANCE_BUCKETS[bucketIdx] + jitter
}

export const getMockTierForMember = (memberId: string | number): LoyaltyTier => {
  return tierFromBalance(getMockBalance(memberId))
}

export const getMockTiersForMembers = (
  memberIds: Array<string | number>
): Record<string, LoyaltyTier> => {
  const result: Record<string, LoyaltyTier> = {}
  for (const id of memberIds) {
    result[String(id)] = getMockTierForMember(id)
  }
  return result
}

export interface MemberLoyalty {
  tier: LoyaltyTier
  /** Spendable balance — net of active holds. */
  balance: number
  /** Gross balance, ignoring holds — gates the redeem entry point so a
   *  pending hold doesn't disable redemption. Defaults to `balance`. */
  availableBalance: number
  /** Tier reachable from `tier` (null when already at top). */
  nextTier: LoyaltyTier | null
  /** Points still needed to reach `nextTier` (0 once already at top). */
  toNextTier: number
  /**
   * 0–1 progress from the current tier threshold to the next. Used to drive
   * the band's progress bar fill width. Always 1 at Platinum.
   */
  progress: number
  /** Threshold at which the next tier begins (or current threshold at top). */
  nextTierThreshold: number
  /** Sum of all points ever credited to the member (always ≥ balance). Mock-only. */
  lifetimeEarned?: number
  /** Sum of all points spent on redemptions. Mock-only. */
  lifetimeUsed?: number
  /** Number of redemptions to date. Mock-only. */
  redemptionsCount?: number
  /** False when the member has never earned (not provisioned upstream yet). */
  hasAccount?: boolean
  /** BE data freshness — 'unavailable' means the upstream platform is down. */
  source?: 'live' | 'stale' | 'unavailable'
}

export const getMockMemberLoyalty = (memberId: string | number): MemberLoyalty => {
  const balance = getMockBalance(memberId)
  const tier = tierFromBalance(balance)
  const next = nextTierFor(tier)
  const currentThreshold = TIER_THRESHOLDS[tier]
  const nextThreshold = next ? TIER_THRESHOLDS[next] : currentThreshold
  const span = nextThreshold - currentThreshold
  const progress = next ? Math.min(1, (balance - currentThreshold) / span) : 1
  // Lifetime is balance plus some deterministic "spent" amount so the rail
  // shows realistic-looking numbers.
  const usedJitter = hash(`used:${String(memberId)}`) % 1800
  const lifetimeUsed = usedJitter
  const lifetimeEarned = balance + lifetimeUsed
  const redemptionsCount = Math.max(0, Math.floor(usedJitter / 350))
  return {
    tier,
    balance,
    // Mock has no holds, so gross == spendable.
    availableBalance: balance,
    nextTier: next,
    nextTierThreshold: nextThreshold,
    toNextTier: next ? Math.max(0, nextThreshold - balance) : 0,
    progress,
    lifetimeEarned,
    lifetimeUsed,
    redemptionsCount,
    hasAccount: true,
    source: 'live',
  }
}

// ─── Earning events ──────────────────────────────────────────────────────

export type EarningEventCategory = 'CHECKIN' | 'CLASS' | 'PT' | 'SWIMMING' | 'PURCHASE' | 'OTHER'

export interface EarningEvent {
  id: string
  category: EarningEventCategory
  /** Display title — e.g. "Class attended — HIIT 60". */
  title: string
  /** Smaller sub-line — instructor, gate, receipt id, etc. */
  subtitle: string
  /** ISO date string at minute resolution. */
  at: string
  branch: string
  /** True when the branch differs from the member's home branch. */
  crossBranch: boolean
  /** Positive integer — points awarded. */
  points: number
  /** Running balance immediately after this event. */
  balanceAfter: number
}

const EVENT_TEMPLATES: Array<{
  category: EarningEventCategory
  title: string
  subtitle: string
  points: number
  branch: string
  crossBranch: boolean
}> = [
  {
    category: 'CLASS',
    title: 'Class attended — HIIT 60',
    subtitle: 'Coach Bua Sirilak',
    points: 25,
    branch: 'Sukhumvit HQ',
    crossBranch: false,
  },
  {
    category: 'CHECKIN',
    title: 'Club check-in',
    subtitle: 'QR scan · entry gate B',
    points: 5,
    branch: 'Sukhumvit HQ',
    crossBranch: false,
  },
  {
    category: 'PT',
    title: 'PT session completed',
    subtitle: 'Trainer Ten Pongpat · session 4 of 10',
    points: 50,
    branch: 'Sukhumvit HQ',
    crossBranch: false,
  },
  {
    category: 'SWIMMING',
    title: 'Swimming session',
    subtitle: 'Lane 3 · 45 minutes',
    points: 15,
    branch: 'Sukhumvit HQ',
    crossBranch: false,
  },
  {
    category: 'CLASS',
    title: 'Class attended — Yoga Flow',
    subtitle: 'Coach Mint Apinya',
    points: 25,
    branch: 'Asok Tower',
    crossBranch: true,
  },
  {
    category: 'PURCHASE',
    title: 'Package purchased — PT 10 Sessions',
    subtitle: 'Receipt #INV-2026-3081 · ฿24,000',
    points: 100,
    branch: 'Sukhumvit HQ',
    crossBranch: false,
  },
  {
    category: 'CLASS',
    title: 'Class attended — Spin 45',
    subtitle: 'Coach Earth Chayakorn',
    points: 25,
    branch: 'Sukhumvit HQ',
    crossBranch: false,
  },
  {
    category: 'CHECKIN',
    title: 'Club check-in',
    subtitle: 'QR scan · entry gate A',
    points: 5,
    branch: 'Sukhumvit HQ',
    crossBranch: false,
  },
  {
    category: 'PT',
    title: 'PT session completed',
    subtitle: 'Trainer Ten Pongpat · session 3 of 10',
    points: 50,
    branch: 'Sukhumvit HQ',
    crossBranch: false,
  },
  {
    category: 'SWIMMING',
    title: 'Swimming session',
    subtitle: 'Lane 5 · 30 minutes',
    points: 15,
    branch: 'Sukhumvit HQ',
    crossBranch: false,
  },
  {
    category: 'CLASS',
    title: 'Class attended — Pilates Reformer',
    subtitle: 'Coach Plue Voraporn',
    points: 25,
    branch: 'Sukhumvit HQ',
    crossBranch: false,
  },
  {
    category: 'CHECKIN',
    title: 'Club check-in',
    subtitle: 'QR scan · entry gate B',
    points: 5,
    branch: 'Sukhumvit HQ',
    crossBranch: false,
  },
]

const NOW_BASE = new Date('2026-05-27T18:32:00').getTime()
const DAY_MS = 86_400_000

export const getMockEarningEvents = (memberId: string | number): EarningEvent[] => {
  const seedHash = hash(`earning:${String(memberId)}`)
  const start = 5 + (seedHash % 8) // pick a slice offset so members differ
  const eventCount = 12 + (seedHash % 8) // 12-19 events per member
  const events: EarningEvent[] = []
  // Walk backward from the most-recent timestamp, attaching balance-after
  // values so the column tells a consistent story.
  let runningBalance = getMockBalance(memberId)
  for (let i = 0; i < eventCount; i++) {
    const tpl = EVENT_TEMPLATES[(start + i) % EVENT_TEMPLATES.length]
    const dayOffset = i * 2 + ((seedHash >> i) % 3)
    const minuteJitter = ((seedHash * (i + 1)) % 1440) * 60_000
    const at = new Date(NOW_BASE - dayOffset * DAY_MS - minuteJitter).toISOString()
    const balanceAfter = runningBalance
    runningBalance -= tpl.points
    events.push({
      id: `evt_${(seedHash + i).toString(36)}`,
      category: tpl.category,
      title: tpl.title,
      subtitle: `${tpl.subtitle} · evt_${(seedHash + i).toString(36).slice(-6)}`,
      at,
      branch: tpl.branch,
      crossBranch: tpl.crossBranch,
      points: tpl.points,
      balanceAfter,
    })
  }
  return events
}

// ─── Redemption history ──────────────────────────────────────────────────

export type RedemptionStatus = 'CONFIRMED' | 'CANCELLED' | 'FAILED' | 'HELD' | 'EXPIRED'

export interface Redemption {
  id: string
  rewardName: string
  /** Short hint shown under the reward name (e.g. "Voucher code redeemed at counter"). */
  rewardSub: string
  cost: number
  status: RedemptionStatus
  /** Reason / extra note for cancelled or failed redemptions. */
  reasonNote?: string
  at: string
  branch: string
  crossBranch: boolean
  code: string
  staff: string
  /** Points credited back after a cancel/expiry/failure (0 for confirmed). */
  pointsReturned?: number
}

const REDEMPTION_TEMPLATES: Array<{
  rewardName: string
  rewardSub: string
  cost: number
  status: RedemptionStatus
  reasonNote?: string
  branch: string
  crossBranch: boolean
  staff: string
}> = [
  {
    rewardName: 'Free smoothie — Tropical Burst',
    rewardSub: 'Voucher code redeemed at counter',
    cost: 150,
    status: 'CONFIRMED',
    branch: 'Sukhumvit HQ',
    crossBranch: false,
    staff: 'Nattaya J.',
  },
  {
    rewardName: 'Guest day pass',
    rewardSub: 'Used by P. Kumkrong · single-day access',
    cost: 300,
    status: 'CONFIRMED',
    branch: 'Asok Tower',
    crossBranch: true,
    staff: 'Phon S.',
  },
  {
    rewardName: 'Free smoothie — Berry Boost',
    rewardSub: 'Member changed mind before fulfillment',
    cost: 150,
    status: 'CANCELLED',
    reasonNote: 'Member cancelled at counter',
    branch: 'Sukhumvit HQ',
    crossBranch: false,
    staff: 'Nattaya J.',
  },
  {
    rewardName: 'Free 30-min PT session',
    rewardSub: 'Trainer Ten unavailable for that slot',
    cost: 500,
    status: 'FAILED',
    reasonNote: 'Trainer Ten unavailable',
    branch: 'Sukhumvit HQ',
    crossBranch: false,
    staff: 'Nattaya J.',
  },
  {
    rewardName: 'Sports towel — FitUP branded',
    rewardSub: 'Collected at front desk',
    cost: 200,
    status: 'CONFIRMED',
    branch: 'Sukhumvit HQ',
    crossBranch: false,
    staff: 'Bee K.',
  },
]

const makeRedemptionCode = (seed: number) => {
  // 6-char alphanumeric in `AA-NNNN` form for the staff-readable code.
  const ALPHA = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const NUM = '0123456789'
  const a1 = ALPHA[seed % ALPHA.length]
  const a2 = ALPHA[(seed >> 5) % ALPHA.length]
  const n1 = NUM[(seed >> 3) % NUM.length]
  const n2 = NUM[(seed >> 7) % NUM.length]
  const n3 = NUM[(seed >> 11) % NUM.length]
  const n4 = NUM[(seed >> 13) % NUM.length]
  return `${a1}${n1}-${n2}${n3}${a2}${n4}`
}

export const getMockRedemptions = (memberId: string | number): Redemption[] => {
  const seed = hash(`redeem:${String(memberId)}`)
  const count = 2 + (seed % 4) // 2-5 redemptions per member
  return Array.from({ length: count }, (_, i) => {
    const tpl = REDEMPTION_TEMPLATES[(seed + i) % REDEMPTION_TEMPLATES.length]
    const dayOffset = 3 + i * 5 + ((seed >> i) % 4)
    const at = new Date(NOW_BASE - dayOffset * DAY_MS).toISOString()
    return {
      id: `red_${(seed + i).toString(36)}`,
      rewardName: tpl.rewardName,
      rewardSub: tpl.rewardSub,
      cost: tpl.cost,
      status: tpl.status,
      reasonNote: tpl.reasonNote,
      at,
      branch: tpl.branch,
      crossBranch: tpl.crossBranch,
      code: makeRedemptionCode(seed + i),
      staff: tpl.staff,
      pointsReturned: tpl.status === 'CONFIRMED' ? 0 : tpl.cost,
    }
  })
}

// ─── Rewards catalog (branch-scoped, but identical across members) ───────

export type RewardCategory = 'SERVICE' | 'CLASS_TRAINING' | 'PRODUCT'

export interface Reward {
  id: string
  name: string
  description: string
  category: RewardCategory
  cost: number
  /** Display chip on the card — "In stock", "12 left", "Booking req.", etc. */
  stockLabel: string
  /** When false the card is rendered as disabled regardless of balance. */
  available: boolean
  /** Remaining redemption quota (null/undefined = unlimited). */
  quota?: number | null
  /** Raw upstream reward type — drives the management-page chip. */
  rewardType?: 'manual' | 'code' | 'perk'
  createdAt?: string
  /** Confirmed redemptions recorded locally (all branches). */
  redemptionCount?: number
}

export const REWARDS_CATALOG: Reward[] = [
  {
    id: 'smoothie',
    name: 'Free smoothie',
    description: 'Any flavor from the smoothie bar. Same-visit only.',
    category: 'SERVICE',
    cost: 150,
    stockLabel: 'In stock',
    available: true,
    quota: 24,
    rewardType: 'code',
    createdAt: '2026-06-01T09:00:00Z',
    redemptionCount: 487,
  },
  {
    id: 'towel',
    name: 'Sports towel',
    description: 'FitUP-branded microfiber towel. 35 × 80 cm.',
    category: 'PRODUCT',
    cost: 200,
    stockLabel: '12 left',
    available: true,
    quota: 12,
    rewardType: 'manual',
    createdAt: '2026-04-18T10:30:00Z',
    redemptionCount: 203,
  },
  {
    id: 'guest',
    name: 'Guest day pass',
    description: '1-day access for one guest. Valid 30 days from issue.',
    category: 'SERVICE',
    cost: 300,
    stockLabel: 'In stock',
    available: true,
    quota: 20,
    rewardType: 'code',
    createdAt: '2026-05-12T14:00:00Z',
    redemptionCount: 156,
  },
  {
    id: 'pt30',
    name: 'Free PT session — 30 min',
    description: 'One personal-training session with an available trainer.',
    category: 'CLASS_TRAINING',
    cost: 500,
    stockLabel: 'Booking req.',
    available: true,
    quota: null,
    rewardType: 'perk',
    createdAt: '2026-03-02T08:00:00Z',
    redemptionCount: 42,
  },
  {
    id: 'class',
    name: 'Free Gold class voucher',
    description: 'One free entry to any Gold-tier class (signature workshops).',
    category: 'CLASS_TRAINING',
    cost: 800,
    stockLabel: 'Booking req.',
    available: true,
    quota: 8,
    rewardType: 'perk',
    createdAt: '2026-05-05T11:15:00Z',
    redemptionCount: 28,
  },
  {
    id: 'spa',
    name: '1-day spa access',
    description: 'Sauna, steam, ice room. Full-day access at home branch only.',
    category: 'SERVICE',
    cost: 1500,
    stockLabel: '3 left',
    available: true,
    quota: 3,
    rewardType: 'manual',
    createdAt: '2026-04-02T16:45:00Z',
    redemptionCount: 11,
  },
  {
    id: 'month',
    name: '1-month tier upgrade',
    description: 'Upgrade to next tier benefits for one month.',
    category: 'SERVICE',
    cost: 2500,
    stockLabel: '—',
    available: false,
    quota: 0,
    rewardType: 'perk',
    createdAt: '2026-02-10T09:30:00Z',
    redemptionCount: 5,
  },
  {
    id: 'annual',
    name: 'Annual renewal discount',
    description: '15% off next annual package renewal.',
    category: 'PRODUCT',
    cost: 5000,
    stockLabel: '—',
    available: false,
    quota: 0,
    rewardType: 'code',
    createdAt: '2026-01-20T13:00:00Z',
    redemptionCount: 2,
  },
]

// ─── Earn preview (QTY-1713/1714/1715) ───────────────────────────────────

export interface EarnPreviewRule {
  unit: string
  value: number
  /** Human-readable rule text, e.g. "1 pt per ฿100 spent". */
  text: string
}

export interface EarnPreviewTierUp {
  willPromote: boolean
  fromTier?: string
  toTier?: string
  balanceAfter?: number
  threshold?: number
}

/** Mirrors BE `LoyaltyEarnPreviewEntity` (GET /loyalty/earn/preview). */
export interface EarnPreview {
  eventType: string
  points: number
  rule: EarnPreviewRule
  /** Only set for spend-rate rules ('THB'). */
  currency?: string
  /** Only present when the preview was member-aware. */
  tierUp?: EarnPreviewTierUp
  /** False when the club disabled this event type — callers should skip the UI. */
  enabled: boolean
}

/** Local fallback of the BE DEFAULT_EARNING_RULES (per-branch config wins live). */
const MOCK_EARNING_RULES: Record<string, { value: number; unit: string }> = {
  'checkin.club': { value: 5, unit: 'per_event' },
  'class.attended': { value: 25, unit: 'per_event' },
  'pt.attended': { value: 50, unit: 'per_session' },
  'swimming.attended': { value: 15, unit: 'per_session' },
  'purchase.package': { value: 1, unit: 'per_100_baht' },
  'purchase.class_booking': { value: 1, unit: 'per_100_baht' },
  'feedback.submitted': { value: 10, unit: 'per_event' },
  'referral.signed_up': { value: 200, unit: 'per_event' },
  'bonus.birthday': { value: 100, unit: 'one_time_year' },
}

export const getMockEarnPreview = (params: {
  eventType: string
  spendAmount?: number
  memberId?: string | number
}): EarnPreview => {
  const rule = MOCK_EARNING_RULES[params.eventType] ?? { value: 10, unit: 'per_event' }
  const isSpendRate = rule.unit === 'per_100_baht'
  const points = isSpendRate ? Math.floor((params.spendAmount ?? 0) / 100) * rule.value : rule.value
  const text = isSpendRate ? `${rule.value} pt per ฿100 spent` : `${rule.value} pts per event`
  let tierUp: EarnPreviewTierUp | undefined
  if (params.memberId != null) {
    const balance = getMockBalance(params.memberId)
    const fromTier = tierFromBalance(balance)
    const toTier = tierFromBalance(balance + points)
    tierUp =
      toTier !== fromTier
        ? {
            willPromote: true,
            fromTier,
            toTier,
            balanceAfter: balance + points,
            threshold: TIER_THRESHOLDS[toTier],
          }
        : { willPromote: false, fromTier }
  }
  return {
    eventType: params.eventType,
    points,
    rule: { unit: rule.unit, value: rule.value, text },
    currency: isSpendRate ? 'THB' : undefined,
    tierUp,
    enabled: true,
  }
}

// ─── Redemption flow (QTY-1712/1716) ─────────────────────────────────────

export type RedemptionBlockReason =
  | 'reward_not_currently_valid'
  | 'tier_locked'
  | 'reward_out_of_stock'
  | 'account_locked'
  | 'insufficient_balance'

/** Mirrors BE `LoyaltyRedeemPreviewEntity` (POST /loyalty/redemptions/preview). */
export interface RedemptionPreviewResult {
  rewardId: string
  redeemable: boolean
  /** Omitted when redeemable. */
  reason?: RedemptionBlockReason
  pointsCost: number
  /** Available balance minus active holds. */
  effectiveBalance: number
}

/** Mirrors BE `LoyaltyHoldEntity` (POST /loyalty/redemptions/hold). */
export interface RedemptionHoldResult {
  redemptionId: string
  /** Staff-friendly XX-YYYYY code — same code the member sees on LINE. */
  code: string
  status: RedemptionStatus
  ttlSeconds: number
  expiresAt: string
  /** BE always sends 0 (no-info sentinel) — real balance arrives at confirm. */
  balanceAfterHold: number
}

/** Mirrors BE `LoyaltyRedemptionReceiptEntity` (confirm / cancel / confirm-by-code). */
export interface RedemptionReceipt {
  redemptionId: string
  status: RedemptionStatus
  rewardName?: string
  pointsUsed?: number
  newBalance?: number
  code?: string
  branchName?: string
  staffName?: string
  confirmedAt?: string
  pointsReturned?: number
}

/** Mirrors BE `LoyaltyRedemptionStatusEntity` (GET /loyalty/redemptions/:id). */
export interface RedemptionStatusResult {
  status: RedemptionStatus
  expiresAt?: string
  remainingSeconds?: number
}

export const getMockRedemptionPreview = (
  memberId: string | number,
  rewardId: string
): RedemptionPreviewResult => {
  const reward = REWARDS_CATALOG.find((r) => r.id === rewardId)
  const balance = getMockBalance(memberId)
  const pointsCost = reward?.cost ?? 0
  let reason: RedemptionBlockReason | undefined
  if (!reward || !reward.available) reason = 'reward_out_of_stock'
  else if (balance < pointsCost) reason = 'insufficient_balance'
  return { rewardId, redeemable: !reason, reason, pointsCost, effectiveBalance: balance }
}

export const getMockRedemptionHold = (
  memberId: string | number,
  rewardId: string
): RedemptionHoldResult => {
  const seed = hash(`hold:${String(memberId)}:${rewardId}:${Date.now()}`)
  return {
    redemptionId: `red_mock_${seed.toString(36)}`,
    code: makeRedemptionCode(seed),
    status: 'HELD',
    ttlSeconds: 900,
    expiresAt: new Date(Date.now() + 900_000).toISOString(),
    balanceAfterHold: 0,
  }
}

export const getMockRedemptionReceipt = (
  redemptionId: string,
  status: RedemptionStatus,
  opts?: { pointsUsed?: number; rewardName?: string; code?: string }
): RedemptionReceipt => ({
  redemptionId,
  status,
  rewardName: opts?.rewardName,
  pointsUsed: status === 'CONFIRMED' ? opts?.pointsUsed : undefined,
  code: opts?.code,
  confirmedAt: status === 'CONFIRMED' ? new Date().toISOString() : undefined,
  pointsReturned: status === 'CANCELLED' || status === 'FAILED' ? opts?.pointsUsed : undefined,
})

// ─── Check-in / attendance response toast block (QTY-1713/1714) ──────────

/**
 * Mirrors BE `LoyaltyToastEntity` — embedded as the optional `loyalty` field
 * of the check-in response (`POST /member/checkin`). `state` semantics:
 * - awarded: synchronous earn succeeded (or rule disabled → pointsAwarded 0)
 * - pending: queued in the BE outbox, upstream call failed — points land later
 * - error:   member has no loyalty mapping — nothing was queued
 */
export interface LoyaltyToastBlock {
  pointsAwarded: number
  balanceAfter: number
  memberName?: string
  activityLabel?: { th: string; en: string }
  /** Tier code (BEGINNING|SILVER|GOLD|PLATINUM) — only on the awarded path. */
  tier?: string
  tierName?: string
  state: 'awarded' | 'pending' | 'error'
  crossBranch?: boolean
  branchName?: string
  tierUp?: { promoted: boolean; fromTier?: string; toTier?: string }
}

// ─── Dashboard summary (QTY-1718) ────────────────────────────────────────

export interface DashboardEventBreakdown {
  eventType: string
  count: number
  points: number
}

export interface DashboardRecentRedemption {
  time: string
  memberName: string
  memberId: string
  reward: string
  points: number
  status: string
}

/** Mirrors BE `LoyaltyDashboardSummaryEntity` (GET /loyalty/dashboard/summary). */
export interface DashboardSummary {
  pointsIssuedToday: {
    totalPoints: number
    eventCount: number
    /** Hardcoded 0 on the BE today. */
    trendVsYesterday: number
    breakdown: DashboardEventBreakdown[]
  }
  redemptionsToday: {
    confirmedCount: number
    onHoldCount: number
    totalPointsRedeemed: number
    /** Last 4 confirmed redemptions, branch-scoped. */
    recent: DashboardRecentRedemption[]
  }
  tierDistribution: {
    totalActiveMembers: number
    promotedThisWeek: number
    /** Tenant-wide counts keyed by tier code (hourly cache — zeros when cold). */
    tiers: Record<string, number>
  }
  source: 'live' | 'stale' | 'unavailable'
  date?: string
}

export const getMockDashboardSummary = (): DashboardSummary => ({
  pointsIssuedToday: {
    totalPoints: 320,
    eventCount: 47,
    trendVsYesterday: 0,
    breakdown: [
      { eventType: 'checkin.club', count: 28, points: 140 },
      { eventType: 'class.attended', count: 5, points: 125 },
      { eventType: 'pt.attended', count: 1, points: 50 },
      { eventType: 'purchase.package', count: 1, points: 5 },
    ],
  },
  redemptionsToday: {
    confirmedCount: 4,
    onHoldCount: 1,
    totalPointsRedeemed: 800,
    recent: [
      {
        time: '17:42',
        memberName: 'Somchai P.',
        memberId: '1042',
        reward: 'Free smoothie',
        points: 150,
        status: 'CONFIRMED',
      },
      {
        time: '16:10',
        memberName: 'Nida K.',
        memberId: '1187',
        reward: 'Guest day pass',
        points: 300,
        status: 'CONFIRMED',
      },
      {
        time: '14:55',
        memberName: 'Beam T.',
        memberId: '1203',
        reward: 'Sports towel',
        points: 200,
        status: 'CONFIRMED',
      },
      {
        time: '11:21',
        memberName: 'Fah W.',
        memberId: '1334',
        reward: 'Free smoothie',
        points: 150,
        status: 'CONFIRMED',
      },
    ],
  },
  tierDistribution: {
    totalActiveMembers: 700,
    promotedThisWeek: 6,
    tiers: { BEGINNING: 238, SILVER: 222, GOLD: 187, PLATINUM: 53 },
  },
  source: 'live',
})

// ─── Redemption approvals (staff queue, GET /loyalty/redemptions) ─────────

export type RedemptionApprovalTab = 'PENDING' | 'APPROVED' | 'REJECTED' | 'ALL'

/** FE shape for a single staff-approval row (adapted from the BE entity). */
export interface RedemptionApprovalItem {
  /** upstream_redemption_id — the id confirm/cancel take. */
  redemptionId: string
  code: string
  memberId: number
  memberName: string
  memberCode: string
  rewardId: string
  rewardName: string
  pointsUsed: number
  status: RedemptionStatus
  pointsReturned: number
  staffName?: string
  createdAt: string
  expiresAt?: string
  /** Resolved member tier — optional (the BE doesn't send it yet; mock-only). */
  tier?: LoyaltyTier
}

export interface RedemptionApprovalsResult {
  items: RedemptionApprovalItem[]
  total: number
  page: number
  pageSize: number
  counts: { pending: number; approved: number; rejected: number }
}

/**
 * Built per-call (not a module const) so the relative "x min ago" timestamps
 * stay anchored to the real current time rather than the fixed NOW_BASE the
 * history mocks use.
 */
const buildApprovalRows = (): RedemptionApprovalItem[] => {
  const now = Date.now()
  return [
    {
      redemptionId: 'red_appr_0142',
      code: 'A1-204C',
      memberId: 104872,
      memberName: 'สมชาย ใจดี',
      memberCode: 'FU-104872',
      rewardId: 'smoothie',
      rewardName: 'Tropical Burst Smoothie',
      pointsUsed: 150,
      status: 'HELD',
      pointsReturned: 0,
      staffName: 'Nattaya J.',
      createdAt: new Date(now - 5 * 60_000).toISOString(),
      expiresAt: new Date(now + 10 * 60_000).toISOString(),
      tier: LoyaltyTier.GOLD,
    },
    {
      redemptionId: 'red_appr_0141',
      code: 'B3-118K',
      memberId: 100214,
      memberName: 'ปริญญา ม่วงเทศ',
      memberCode: 'FU-100214',
      rewardId: 'towel',
      rewardName: 'Sports Towel',
      pointsUsed: 200,
      status: 'HELD',
      pointsReturned: 0,
      staffName: 'Phon S.',
      createdAt: new Date(now - 12 * 60_000).toISOString(),
      expiresAt: new Date(now + 3 * 60_000).toISOString(),
      tier: LoyaltyTier.SILVER,
    },
    {
      redemptionId: 'red_appr_0140',
      code: 'C5-983P',
      memberId: 98331,
      memberName: 'Kanyarat S.',
      memberCode: 'FU-098331',
      rewardId: 'pt30',
      rewardName: '30-min PT Session',
      pointsUsed: 500,
      status: 'HELD',
      pointsReturned: 0,
      staffName: 'Bee K.',
      createdAt: new Date(now - 65 * 60_000).toISOString(),
      expiresAt: new Date(now - 5 * 60_000).toISOString(),
      tier: LoyaltyTier.PLATINUM,
    },
    {
      redemptionId: 'red_appr_0137',
      code: 'D2-771Y',
      memberId: 104102,
      memberName: 'พิชญา รุ่งเรือง',
      memberCode: 'FU-104102',
      rewardId: 'class',
      rewardName: 'Free Gold class voucher',
      pointsUsed: 800,
      status: 'CONFIRMED',
      pointsReturned: 0,
      staffName: 'Nattaya J.',
      createdAt: new Date(now - 180 * 60_000).toISOString(),
      tier: LoyaltyTier.GOLD,
    },
    {
      redemptionId: 'red_appr_0136',
      code: 'E8-330R',
      memberId: 103580,
      memberName: 'รุ่งทิวา แก้วดี',
      memberCode: 'FU-103580',
      rewardId: 'guest',
      rewardName: 'Guest Day Pass',
      pointsUsed: 300,
      status: 'CANCELLED',
      pointsReturned: 300,
      staffName: 'Bee K.',
      createdAt: new Date(now - 240 * 60_000).toISOString(),
      tier: LoyaltyTier.BEGINNING,
    },
  ]
}

const APPROVAL_TAB_STATUSES: Record<RedemptionApprovalTab, RedemptionStatus[] | null> = {
  PENDING: ['HELD'],
  APPROVED: ['CONFIRMED'],
  REJECTED: ['CANCELLED', 'FAILED', 'EXPIRED'],
  ALL: null,
}

export const getMockRedemptionApprovals = (params: {
  status: RedemptionApprovalTab
  search?: string
  page?: number
  pageSize?: number
}): RedemptionApprovalsResult => {
  const rows = buildApprovalRows()
  const page = params.page ?? 1
  const pageSize = params.pageSize ?? 20
  const counts = {
    pending: rows.filter((r) => r.status === 'HELD').length,
    approved: rows.filter((r) => r.status === 'CONFIRMED').length,
    rejected: rows.filter(
      (r) => r.status === 'CANCELLED' || r.status === 'FAILED' || r.status === 'EXPIRED'
    ).length,
  }
  const allowed = APPROVAL_TAB_STATUSES[params.status]
  const q = params.search?.trim().toLowerCase()
  const matched = rows.filter((r) => {
    if (allowed && !allowed.includes(r.status)) return false
    // Mirror the live BE (findManyByBranch), which searches code, rewardName
    // and the upstream redemption id — NOT member name/code (member identity
    // is resolved after paging server-side). Keep these in lockstep so search
    // behaves the same in mock and against the real wire.
    if (
      q &&
      !r.rewardName.toLowerCase().includes(q) &&
      !r.code.toLowerCase().includes(q) &&
      !r.redemptionId.toLowerCase().includes(q)
    ) {
      return false
    }
    return true
  })
  const start = (page - 1) * pageSize
  return {
    items: matched.slice(start, start + pageSize),
    total: matched.length,
    page,
    pageSize,
    counts,
  }
}

// Re-export so callers can iterate filter options without importing two files.
export { TIER_ORDER }
